import { useState, useEffect } from 'react';
import { useLang } from '../../context/LangContext';
import { supabase } from '../../lib/supabase';
import { COLORS } from '../../lib/constants';

export default function BusinessDetail({ business, onBook, onBack }) {
  const { isRtl } = useLang();
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState('services');
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [step, setStep] = useState('services'); // services → staff → confirm

  useEffect(() => {
    const fetchAll = async () => {
      const [sRes, stRes, rRes] = await Promise.all([
        supabase.from('services').select('*').eq('business_id', business.id).eq('is_active', true),
        supabase.from('staff').select('*').eq('business_id', business.id).eq('is_active', true),
        supabase.from('reviews').select('*').eq('business_id', business.id).order('created_at', { ascending: false }).limit(10),
      ]);
      setServices(sRes.data || []);
      setStaff(stRes.data || []);
      setReviews(rRes.data || []);
      setLoading(false);
    };
    fetchAll();
  }, [business.id]);

  const handleSelectService = async (service) => {
    setSelectedService(service);
    // جلب الخبراء المرتبطين بهذه الخدمة
    const { data: staffServices } = await supabase
      .from('staff_services')
      .select('staff_id')
      .eq('service_id', service.id);
    
    if (staffServices && staffServices.length > 0) {
      const staffIds = staffServices.map(ss => ss.staff_id);
      const linked = staff.filter(s => staffIds.includes(s.id));
      setAvailableStaff(linked.length > 0 ? linked : staff);
    } else {
      setAvailableStaff(staff);
    }
    setStep('staff');
  };

  const handleSelectStaff = (staffMember) => {
    onBook({ business, service: selectedService, staff: staffMember });
  };

  const name = isRtl ? business.name_ar : (business.name_en || business.name_ar);
  const desc = isRtl ? business.description_ar : (business.description_en || business.description_ar);

  const TABS = [
    { id: 'services', labelAr: 'الخدمات', labelEn: 'Services' },
    { id: 'staff', labelAr: 'الفريق', labelEn: 'Team' },
    { id: 'reviews', labelAr: 'التقييمات', labelEn: 'Reviews' },
  ];

  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: 'Cairo, sans-serif', direction: isRtl ? 'rtl' : 'ltr' }}>
      {/* الغلاف */}
      <div style={{ height: 200, background: business.cover_url ? `url(${business.cover_url}) center/cover` : `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`, position: 'relative' }}>
        <button onClick={step === 'staff' ? () => setStep('services') : onBack} style={{ position: 'absolute', top: 16, right: isRtl ? 'auto' : 16, left: isRtl ? 16 : 'auto', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'Cairo, sans-serif', fontWeight: 600 }}>
          {isRtl ? '→ رجوع' : '← Back'}
        </button>
      </div>

      {/* معلومات الصالون */}
      <div style={{ background: '#fff', padding: '16px 16px 0', marginTop: -20, borderRadius: '20px 20px 0 0' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ width: 72, height: 72, borderRadius: 16, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0, overflow: 'hidden', marginTop: -36, border: `3px solid #fff`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {business.logo_url ? <img src={business.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '🏪'}
          </div>
          <div style={{ flex: 1, paddingTop: 4 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.text }}>{name}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>📍 {business.district ? `${business.district}، ` : ''}{business.city}</div>
            {business.rating > 0 && (
              <div style={{ fontSize: 13, color: '#F59E0B', fontWeight: 700, marginTop: 4 }}>
                ⭐ {Number(business.rating).toFixed(1)} ({business.reviews_count} {isRtl ? 'تقييم' : 'reviews'})
              </div>
            )}
          </div>
        </div>
        {desc && <p style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.7, marginBottom: 16 }}>{desc}</p>}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {business.phone && (
            <a href={`tel:${business.phone}`} style={{ fontSize: 12, background: COLORS.primaryLight, color: COLORS.primary, padding: '6px 12px', borderRadius: 10, fontWeight: 600, textDecoration: 'none' }}>
              📞 {isRtl ? 'اتصال' : 'Call'}
            </a>
          )}
          {business.whatsapp && (
            <a href={`https://wa.me/${business.whatsapp}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, background: '#F0FDF4', color: '#16A34A', padding: '6px 12px', borderRadius: 10, fontWeight: 600, textDecoration: 'none' }}>
              💬 WhatsApp
            </a>
          )}
          {business.lat && business.lng && (
            <a href={`https://maps.google.com/?q=${business.lat},${business.lng}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, background: '#E8F5E9', color: '#2E7D32', padding: '6px 12px', borderRadius: 10, fontWeight: 600, textDecoration: 'none' }}>
              📍 {isRtl ? 'الموقع' : 'Location'}
            </a>
          )}
        </div>

        {/* تبويبات — تظهر فقط في الخطوة الأولى */}
        {step === 'services' && (
          <div style={{ display: 'flex', borderBottom: `2px solid ${COLORS.border}` }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: '12px 0', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif', color: tab === t.id ? COLORS.primary : COLORS.textMuted, borderBottom: tab === t.id ? `2px solid ${COLORS.primary}` : '2px solid transparent', marginBottom: -2 }}>
                {isRtl ? t.labelAr : t.labelEn}
              </button>
            ))}
          </div>
        )}

        {/* عنوان اختيار الخبير */}
        {step === 'staff' && (
          <div style={{ padding: '12px 0', borderBottom: `2px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.text }}>
              {isRtl ? `اختر الخبير لخدمة: ${selectedService?.name_ar}` : `Choose expert for: ${selectedService?.name_en || selectedService?.name_ar}`}
            </div>
          </div>
        )}
      </div>

      {/* المحتوى */}
      <div style={{ padding: 16 }}>
        {loading && <div style={{ textAlign: 'center', padding: 30, color: COLORS.textMuted }}>{isRtl ? 'جارٍ التحميل...' : 'Loading...'}</div>}

        {/* الخدمات */}
        {!loading && step === 'services' && tab === 'services' && (
          services.length === 0
            ? <div style={{ textAlign: 'center', padding: 30, color: COLORS.textMuted }}>{isRtl ? 'لا توجد خدمات' : 'No services'}</div>
            : services.map(s => (
              <div key={s.id} style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{isRtl ? s.name_ar : (s.name_en || s.name_ar)}</div>
                  {(isRtl ? s.description_ar : s.description_en) && (
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{isRtl ? s.description_ar : s.description_en}</div>
                  )}
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 3 }}>⏱ {s.duration_minutes} {isRtl ? 'دقيقة' : 'min'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.primary }}>{s.price} {isRtl ? 'ر.س' : 'SAR'}</div>
                  <button onClick={() => handleSelectService(s)} style={{ background: COLORS.primary, border: 'none', borderRadius: 10, padding: '7px 14px', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                    {isRtl ? 'احجز' : 'Book'}
                  </button>
                </div>
              </div>
            ))
        )}

        {/* اختيار الخبير */}
        {!loading && step === 'staff' && (
          availableStaff.length === 0
            ? (
              <div style={{ textAlign: 'center', padding: 30 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>👤</div>
                <div style={{ color: COLORS.textMuted, fontSize: 14 }}>{isRtl ? 'لا يوجد خبراء متاحون' : 'No experts available'}</div>
                <button onClick={() => onBook({ business, service: selectedService, staff: null })} style={{ marginTop: 16, background: COLORS.primary, border: 'none', borderRadius: 12, padding: '10px 24px', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                  {isRtl ? 'تابع بدون اختيار خبير' : 'Continue without expert'}
                </button>
              </div>
            )
            : availableStaff.map(s => (
              <div key={s.id} onClick={() => handleSelectStaff(s)} style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${COLORS.border}`, cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'center', transition: 'all .2s' }}>
                {/* صورة الخبير */}
                <div style={{ width: 72, height: 72, borderRadius: 16, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, overflow: 'hidden', flexShrink: 0 }}>
                  {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, marginBottom: 3 }}>{isRtl ? s.name_ar : (s.name_en || s.name_ar)}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>{isRtl ? s.title_ar : (s.title_en || s.title_ar)}</div>
                  {/* التقييم */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {s.rating > 0 ? (
                      <>
                        <div style={{ display: 'flex', gap: 1 }}>
                          {[1,2,3,4,5].map(star => (
                            <span key={star} style={{ fontSize: 14, color: star <= Math.round(s.rating) ? '#F59E0B' : '#DDD' }}>★</span>
                          ))}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B' }}>{Number(s.rating).toFixed(1)}</span>
                        <span style={{ fontSize: 11, color: COLORS.textMuted }}>({s.total_reviews || s.reviews_count || 0} {isRtl ? 'تقييم' : 'reviews'})</span>
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? 'لا توجد تقييمات بعد' : 'No reviews yet'}</span>
                    )}
                  </div>
                  {s.experience_years > 0 && (
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 3 }}>
                      🏆 {s.experience_years} {isRtl ? 'سنوات خبرة' : 'years experience'}
                    </div>
                  )}
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: 20 }}>{isRtl ? '‹' : '›'}</div>
              </div>
            ))
        )}

        {/* الفريق — عرض فقط */}
        {!loading && step === 'services' && tab === 'staff' && (
          staff.length === 0
            ? <div style={{ textAlign: 'center', padding: 30, color: COLORS.textMuted }}>{isRtl ? 'لا يوجد فريق' : 'No staff'}</div>
            : staff.map(s => (
              <div key={s.id} style={{ background: '#fff', borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${COLORS.border}`, display: 'flex', gap: 14, alignItems: 'center' }}>
                <div style={{ width: 64, height: 64, borderRadius: 14, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, overflow: 'hidden' }}>
                  {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{isRtl ? s.name_ar : (s.name_en || s.name_ar)}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? s.title_ar : (s.title_en || s.title_ar)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    {s.rating > 0 ? (
                      <>
                        {[1,2,3,4,5].map(star => <span key={star} style={{ fontSize: 12, color: star <= Math.round(s.rating) ? '#F59E0B' : '#DDD' }}>★</span>)}
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B', marginRight: 4 }}>{Number(s.rating).toFixed(1)}</span>
                      </>
                    ) : <span style={{ fontSize: 12, color: COLORS.textMuted }}>{isRtl ? 'لا توجد تقييمات' : 'No reviews yet'}</span>}
                  </div>
                </div>
              </div>
            ))
        )}

        {/* التقييمات */}
        {!loading && step === 'services' && tab === 'reviews' && (
          reviews.length === 0
            ? <div style={{ textAlign: 'center', padding: 30, color: COLORS.textMuted }}>{isRtl ? 'لا توجد تقييمات بعد' : 'No reviews yet'}</div>
            : reviews.map(r => (
              <div key={r.id} style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, border: `1px solid ${COLORS.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>{r.customer_name || (isRtl ? 'مستخدم' : 'User')}</div>
                  <div style={{ display: 'flex', gap: 1 }}>
                    {[1,2,3,4,5].map(star => <span key={star} style={{ color: star <= r.rating ? '#F59E0B' : '#DDD', fontSize: 14 }}>★</span>)}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>{r.comment}</div>
              </div>
            ))
        )}

        <div style={{ height: 80 }} />
      </div>
    </div>
  );
}
